// HOOKS
import { useLazyQuery } from "@apollo/client";
import { useState, useEffect } from "react";
import useQuery from "../other/useQuery.other.hook";
import useDownloadCSV from "../other/useDownloadCSV.other.hook";

// CONSTANTS
import { IPAD_DEVICES } from "../../constants/devices/ipad.device.constant";
import { MACBOOK_DEVICES } from "../../constants/devices/macbook.device.constant";
import { PS5_DEVICES } from "../../constants/devices/ps5.device.constant";
import { XBOX_DEVICES } from "../../constants/devices/xbox.device.constant";
import { SURFACE_DEVICES } from "../../constants/devices/surface.device.constant";

// GRAPHQL
import { GET_SQUARETRADE_REPORT } from "../../graphql/queries/tickets/squareTradeReport.tickets.queries";

const DEFAULT_QUERY = {
  search: "",
  page: 1,
  limit: 0,
  sort: "-updated_at",
};

const getAssetType = (asset_model) => {
  if (!asset_model) return "";
  if (IPAD_DEVICES.includes(asset_model)) return "iPad";
  if (MACBOOK_DEVICES.includes(asset_model)) return "Macbook";
  if (PS5_DEVICES.includes(asset_model)) return "PS5";
  if (XBOX_DEVICES.includes(asset_model)) return "Xbox";
  if (SURFACE_DEVICES.includes(asset_model)) return "Surface";
  return "Other";
};

const REPORT_HEADERS = [
  "Ticket #",
  "Status",
  "Received Date",
  "Claim Accepted Date",
  "Claim ID",
  "Asset Type",
  "Make",
  "Model",
];

const useGetSquareTradeTickets = () => {
  const handleDownload = useDownloadCSV();
  const [squareTradeTickets, setSquareTradeTickets] = useState([]);
  const [metaData, setMetaData] = useState({});
  const [ticketInput, setTicketInput] = useState({});
  const [getSquareTradeTicketsQuery, { loading, error }] = useLazyQuery(
    GET_SQUARETRADE_REPORT,
    {
      fetchPolicy: "no-cache",
    }
  );
  const {
    query,
    setQuery,
    handleLimitChange,
    handlePageChange,
    handleSearch,
    handleSort,
  } = useQuery({ defaultQuery: DEFAULT_QUERY });

  const getSquareTradeTickets = async () => {
    try {
      const res = await getSquareTradeTicketsQuery({
        variables: { query, ticket: ticketInput },
      });
      if (res?.data?.tickets?.tickets) {
        setSquareTradeTickets(
          res.data.tickets.tickets.map((ticket) => ({
            ...ticket,
          }))
        );
      }
      if (res?.data?.tickets?.metaData) setMetaData(res.data.tickets.metaData);
    } catch (error) {
      console.log(`Error getting square trade tickets: ${error?.message}`);
      alert(`Error getting square trade tickets: ${error?.message}`);
    }
  };

  const generateSquareTradeReport = (tickets, filename = "Square Trade") => {
    try {
      const data = [];
      if (!tickets || !tickets.length) return console.log("No tickets found");
      for (let i = 0; i < tickets.length; i++) {
        const ticket = tickets[i];
        const assetType = getAssetType(ticket.assets?.[0]?.properties?.Model);
        data.push([
          ticket.number || "-",
          ticket.status || "-",
          ticket.properties?.received_date || "-",
          ticket.properties?.claim_accepted_date || "-",
          ticket.properties?.claim_id || "-",
          assetType,
          ticket.assets?.[0]?.properties?.Make || "-",
          ticket.assets?.[0]?.properties?.Model || "-",
        ]);
      }
      handleDownload({ data, headers: REPORT_HEADERS, filename });
    } catch (err) {
      console.error(`Error generating square trade report: ${err?.message}`);
      alert(`Error generating square trade report: ${err?.message}`);
    }
  };

  useEffect(() => {
    if (Object.entries(ticketInput).length) {
      getSquareTradeTickets();
    }
  }, [query, ticketInput]);

  return {
    squareTradeTickets,
    metaData,
    loading,
    error,
    query,
    setQuery,
    handleLimitChange,
    handlePageChange,
    handleSearch,
    handleSort,
    setTicketInput,
    generateSquareTradeReport,
  };
};

export default useGetSquareTradeTickets;
